import { ReactNode, useCallback, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { PopoverCombobox, PopoverComboboxOption } from '@/components/ui/custom/PopoverCombobox';
import { FormControl, FormItem, FormLabel } from '@/components/ui/form';
import { CartesiaIcon } from '@/modules/developerTools/icons/Cartesia';
import { ElevenLabsIcon } from '@/modules/developerTools/icons/ElevenLabs';
import { MiniMaxIcon } from '@/modules/developerTools/icons/MiniMax';
import { TTSModelInfo, TTSProvider } from '@/shared/types/voiceProviders';

const PROVIDER_NAMES: Record<TTSProvider, string> = {
  elevenlabs: 'ElevenLabs',
  cartesia: 'Cartesia',
  minimax: 'MiniMax',
};

const PROVIDER_ICONS: Record<TTSProvider, ReactNode> = {
  elevenlabs: <ElevenLabsIcon className="size-4" />,
  cartesia: <CartesiaIcon className="size-4" />,
  minimax: <MiniMaxIcon className="size-4" />,
};

interface TTSModelSelectorProps {
  value: string;
  onChange: (modelId: string, provider: TTSProvider) => void;
  models: TTSModelInfo[];
  disabled?: boolean;
}

export function TTSModelSelector({ value, onChange, models, disabled }: TTSModelSelectorProps) {
  const { t } = useTranslation();

  const options: PopoverComboboxOption[] = useMemo(() => {
    return models.map((model) => ({
      value: model.id,
      label: `${PROVIDER_NAMES[model.provider]} · ${model.name}`,
      icon: PROVIDER_ICONS[model.provider],
    }));
  }, [models]);

  const handleChange = useCallback(
    (modelId: string) => {
      const model = models.find((m) => m.id === modelId);
      if (model) {
        onChange(modelId, model.provider);
      }
    },
    [models, onChange]
  );

  return (
    <FormItem className="flex flex-col">
      <FormLabel>{t('integrations.voice_common.voice.model')}</FormLabel>
      <FormControl>
        <PopoverCombobox
          options={options}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          contentClassName="w-80"
        />
      </FormControl>
    </FormItem>
  );
}
